import { useState, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

/**
 * A hover/focus tooltip.
 *
 * Deliberately CSS-positioned rather than portalled: every use in the console
 * is inside a scroll container, and a portalled tooltip would detach from its
 * trigger as the container scrolls.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  className,
}: {
  content: ReactNode
  children: ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
}) {
  const [visible, setVisible] = useState(false)

  const positions = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-1.5',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-1.5',
    left: 'right-full top-1/2 -translate-y-1/2 mr-1.5',
    right: 'left-full top-1/2 -translate-y-1/2 ml-1.5',
  }

  if (!content) return <>{children}</>

  return (
    <span
      className={cn('relative inline-flex', className)}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      {children}
      {visible && (
        <span
          role="tooltip"
          className={cn(
            'bg-foreground text-background elevated pointer-events-none absolute z-50',
            'animate-fade-in w-max max-w-60 rounded-md px-2 py-1 text-[11.5px] leading-snug font-medium',
            'whitespace-normal',
            positions[side],
          )}
        >
          {content}
        </span>
      )}
    </span>
  )
}
